import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import QualifiedSpendTagPicker from 'components/QualifiedSpendTagPicker';
import { Icon } from 'utils/icons';

import './styles.scss';

const EMPTY_FORM = {
  name: '',
  ratePercent: '',
  source: 'tags',
  tagIds: [],
  manualAmount: '',
  minimumSpend: '',
  maximumCredit: '',
  realizationPercent: '100',
  estimatedCosts: '0',
};

const displayDecimal = value => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : '';
};

const optionalNumber = value => (value === '' ? null : Number(value));

const isPercent = value =>
  value !== '' &&
  Number.isFinite(Number(value)) &&
  Number(value) >= 0 &&
  Number(value) <= 100;

const isNonNegative = value =>
  value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

const isOptionalNonNegative = value => value === '' || isNonNegative(value);

const formFromIncentive = incentive => ({
  name: incentive.name || '',
  ratePercent: displayDecimal(incentive.rate_percent),
  source:
    incentive.manual_eligible_amount !== null &&
    incentive.manual_eligible_amount !== undefined
      ? 'manual'
      : 'tags',
  tagIds: (incentive.eligible_tags || []).map(tag => Number(tag.id)),
  manualAmount: displayDecimal(incentive.manual_eligible_amount),
  minimumSpend: displayDecimal(incentive.minimum_qualified_spend),
  maximumCredit: displayDecimal(incentive.maximum_credit),
  realizationPercent: displayDecimal(incentive.realization_percent ?? 100),
  estimatedCosts: displayDecimal(incentive.estimated_costs ?? 0),
});

const FIELD_HELP = {
  creditRate:
    'The percentage applied to qualified spend to calculate the gross credit.',
  realization:
    'The percentage of the calculated credit you expect to actually receive. Use less than 100% for transfer discounts or other reductions.',
  qualifiedSpend:
    'The budget costs eligible for this credit. Calculate them from selected tags or enter the amount manually.',
  minimumSpend:
    'The qualified-spend threshold that must be reached before the credit is estimated.',
  creditCap:
    'The maximum credit this calculation line can produce, regardless of qualified spend.',
  estimatedFees:
    'Expected application, audit, brokerage, or other costs deducted from the calculated credit.',
};

const FieldHelp = ({ label, children }) => {
  const helpId = React.useId();

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      delay={{ show: 150, hide: 75 }}
      placement="top"
      flip
      overlay={
        <Popover
          id={`film-incentive-field-help-${helpId}`}
          className="film-incentive-field-help-popover"
        >
          <Popover.Body>{children}</Popover.Body>
        </Popover>
      }
    >
      <button
        type="button"
        className="film-incentive-form__help"
        aria-label={`${label} information`}
      >
        <Icon name="InfoCircleIcon" aria-hidden="true" />
      </button>
    </OverlayTrigger>
  );
};

FieldHelp.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const NumericField = ({
  label,
  helpText,
  value,
  onChange,
  suffix,
  optional,
  max,
  disabled,
  required,
}) => {
  const inputId = React.useId();

  return (
    <div className="film-incentive-form__field">
      <div className="film-incentive-form__label-row">
        <label htmlFor={inputId}>
          {label} {optional && <small>(optional)</small>}
        </label>
        {helpText && <FieldHelp label={label}>{helpText}</FieldHelp>}
      </div>
      <div className="film-incentive-form__number">
        <input
          id={inputId}
          className="film-incentive-form__input"
          type="number"
          min="0"
          max={max}
          step="0.01"
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
        />
        {suffix && (
          <span className="film-incentive-form__suffix">{suffix}</span>
        )}
      </div>
    </div>
  );
};

NumericField.propTypes = {
  label: PropTypes.string.isRequired,
  helpText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  suffix: PropTypes.string,
  optional: PropTypes.bool,
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

NumericField.defaultProps = {
  helpText: '',
  value: '',
  suffix: '',
  optional: false,
  max: undefined,
  disabled: false,
  required: false,
};

const FilmIncentiveForm = ({
  incentive,
  budgetId,
  tags,
  onSave,
  onCancel,
  onDelete,
  isSaving,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    setForm(incentive ? formFromIncentive(incentive) : EMPTY_FORM);
  }, [incentive]);

  const setField = (field, value) =>
    setForm(current => ({ ...current, [field]: value }));

  const isValid =
    form.name.trim() &&
    isPercent(form.ratePercent) &&
    isPercent(form.realizationPercent) &&
    isNonNegative(form.estimatedCosts || 0) &&
    isOptionalNonNegative(form.minimumSpend) &&
    isOptionalNonNegative(form.maximumCredit) &&
    ((form.source === 'tags' && form.tagIds.length > 0) ||
      (form.source === 'manual' && isNonNegative(form.manualAmount)));

  const handleSubmit = async event => {
    event.preventDefault();
    if (!isValid || isSaving) return;
    await onSave({
      ...(incentive?.id ? { id: incentive.id } : {}),
      budget: Number(budgetId),
      name: form.name.trim(),
      rate_percent: Number(form.ratePercent),
      eligible_tag_ids: form.source === 'tags' ? form.tagIds : [],
      manual_eligible_amount:
        form.source === 'manual' ? Number(form.manualAmount) : null,
      minimum_qualified_spend: optionalNumber(form.minimumSpend),
      maximum_credit: optionalNumber(form.maximumCredit),
      realization_percent: Number(form.realizationPercent),
      estimated_costs: Number(form.estimatedCosts || 0),
    });
  };

  return (
    <form className="film-incentive-form" onSubmit={handleSubmit}>
      <div className="film-incentive-form__grid">
        <label className="film-incentive-form__field film-incentive-form__field--name">
          <span>Credit name</span>
          <input
            className="film-incentive-form__input"
            autoFocus
            type="text"
            value={form.name}
            placeholder="Georgia Base Credit"
            maxLength={150}
            onChange={event => setField('name', event.target.value)}
            disabled={isSaving}
            required
          />
        </label>
        <NumericField
          label="Credit rate"
          helpText={FIELD_HELP.creditRate}
          value={form.ratePercent}
          suffix="%"
          max="100"
          required
          disabled={isSaving}
          onChange={event => setField('ratePercent', event.target.value)}
        />
        <NumericField
          label="Realization"
          helpText={FIELD_HELP.realization}
          value={form.realizationPercent}
          suffix="%"
          max="100"
          required
          disabled={isSaving}
          onChange={event => setField('realizationPercent', event.target.value)}
        />
      </div>

      <fieldset className="film-incentive-form__source" disabled={isSaving}>
        <legend>
          <span className="film-incentive-form__label-row">
            <span>Qualified spend</span>
            <FieldHelp label="Qualified spend">
              {FIELD_HELP.qualifiedSpend}
            </FieldHelp>
          </span>
        </legend>
        <label>
          <input
            type="radio"
            checked={form.source === 'tags'}
            onChange={() => setField('source', 'tags')}
          />
          Calculate from tagged rows
        </label>
        <label>
          <input
            type="radio"
            checked={form.source === 'manual'}
            onChange={() => setField('source', 'manual')}
          />
          Enter qualified spend manually
        </label>
      </fieldset>

      {form.source === 'tags' ? (
        <QualifiedSpendTagPicker
          budgetId={budgetId}
          tags={tags}
          selectedTagIds={form.tagIds}
          onChange={tagIds => setField('tagIds', tagIds)}
          disabled={isSaving}
        />
      ) : (
        <NumericField
          label="Manual qualified spend"
          value={form.manualAmount}
          required
          disabled={isSaving}
          onChange={event => setField('manualAmount', event.target.value)}
        />
      )}

      <div className="film-incentive-form__grid film-incentive-form__grid--options">
        <NumericField
          label="Minimum spend"
          helpText={FIELD_HELP.minimumSpend}
          value={form.minimumSpend}
          optional
          disabled={isSaving}
          onChange={event => setField('minimumSpend', event.target.value)}
        />
        <NumericField
          label="Credit cap"
          helpText={FIELD_HELP.creditCap}
          value={form.maximumCredit}
          optional
          disabled={isSaving}
          onChange={event => setField('maximumCredit', event.target.value)}
        />
        <NumericField
          label="Estimated fees"
          helpText={FIELD_HELP.estimatedFees}
          value={form.estimatedCosts}
          disabled={isSaving}
          onChange={event => setField('estimatedCosts', event.target.value)}
        />
      </div>

      <div className="film-incentive-form__actions">
        {incentive && onDelete && (
          <button
            type="button"
            className="danger-link"
            onClick={onDelete}
            disabled={isSaving}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          className="secondary-button"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="primary-button"
          disabled={!isValid || isSaving}
        >
          {isSaving ? 'Saving…' : 'Save estimate'}
        </button>
      </div>
    </form>
  );
};

FilmIncentiveForm.propTypes = {
  incentive: PropTypes.object,
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  tags: PropTypes.arrayOf(PropTypes.object),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  isSaving: PropTypes.bool,
};

FilmIncentiveForm.defaultProps = {
  incentive: null,
  tags: [],
  onDelete: null,
  isSaving: false,
};

export default FilmIncentiveForm;
